import { type KeyboardEvent, useRef, useState } from "react";
import type { Entry, Track } from "./api";
import { accessibleName, isFuzzy, whenLabel } from "./dates";
import { EntryCardBody } from "./entry-card";
import {
  axisRange,
  axisWidth,
  datedInOrder,
  LABEL_MAX,
  labelWidth,
  type PlacedEntry,
  placeHorizontal,
  ROW_STEP,
  verticalExtent,
  verticalSide,
  yearTicks,
} from "./frise-layout";
import { useWideScreen } from "./use-wide-screen";

/**
 * La frise (spec, sections 1 et 3.3 ; planches `FriseConstellation` et `Main`). Un seul modele,
 * deux mises en page : a partir de 768 px le fil horizontal, en dessous le fil vertical alterne.
 * Chaque boule est un vrai bouton (cible de 44 px, nom accessible « titre, date ») ; sa carte
 * s'ouvre au survol, au focus et au clic (bureau) ou au toucher (telephone, feuille en bas).
 */
export type FriseProps = {
  entries: readonly Entry[];
  tracks: readonly Track[];
  birthDate: string | null;
  today: string;
  /** Absent : la frise est en lecture seule (la vue d'un proche), sans « Modifier ». */
  onEdit?: ((entry: Entry) => void) | undefined;
};

export function Frise(props: FriseProps) {
  const wide = useWideScreen();
  return wide ? <FriseHorizontal {...props} /> : <FriseVertical {...props} />;
}

const trackOf = (tracks: readonly Track[], entry: Entry) =>
  tracks.find((track) => track.id === entry.trackId);

const colorOf = (tracks: readonly Track[], entry: Entry) =>
  trackOf(tracks, entry)?.color ?? "#AEB6C8";

/** L'allure d'une boule : pleine pour une date sure, cerclee en pointille pour une date floue. */
function ballStyle(color: string, fuzzy: boolean, selected: boolean) {
  return {
    background: fuzzy ? `${color}33` : color,
    border: fuzzy ? `2px dashed ${color}` : "2px solid rgba(255,255,255,0.85)",
    boxShadow: selected
      ? `0 0 0 3px #0D1222, 0 0 0 5px ${color}, 0 0 26px ${color}`
      : `0 0 14px ${color}`,
  };
}

/** Le ciel seme de points, toujours le meme (positions calculees, pas tirees au hasard). */
function stars(count: number, width: number, height: number, avoidY: number) {
  return Array.from({ length: count }, (_, k) => {
    let y = (k * 53.7) % height;
    if (Math.abs(y - avoidY) < 24) y = (y + 90) % height;
    return {
      key: k,
      x: ((k * 37.3) % 100) * (width / 100),
      y,
      r: k % 5 === 0 ? 1.5 : 1,
      opacity: 0.15 + (k % 4) * 0.12,
    };
  });
}

/** Marge de chaque cote de l'axe, pour qu'une boule au bord garde sa cible entiere. */
const SIDE = 40;
const CARD_WIDTH = 300;

/**
 * La carte se pose apres le libelle de la boule, pour ne pas le cacher ; contre le bord droit,
 * elle passe de l'autre cote de la boule.
 */
function cardLeft(item: PlacedEntry, total: number): number {
  const ball = SIDE + item.x;
  const label = labelWidth(item.entry) + 40;
  const right = item.labelSide === "right" ? ball + 22 + label : ball + 30;
  if (right + CARD_WIDTH <= total) return right;
  const left = item.labelSide === "left" ? ball - 22 - label - CARD_WIDTH : ball - 30 - CARD_WIDTH;
  return Math.max(0, left);
}

function FriseHorizontal({ entries, tracks, birthDate, today, onEdit }: FriseProps) {
  const range = axisRange(entries, birthDate, today);
  const width = axisWidth(range, 1000);
  const placed = placeHorizontal(entries, range, width, today);
  const { threadY, height: drawn } = verticalExtent(placed.map((item) => item.level));
  const height = Math.max(drawn, 340);
  const ticks = yearTicks(range, width);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const buttons = useRef(new Map<number, HTMLButtonElement>());
  const selected = placed.find((item) => item.entry.id === selectedId) ?? null;
  const total = width + SIDE * 2;
  const ballY = (item: PlacedEntry) => threadY - item.level * ROW_STEP;

  // Fermer rend le focus a la boule : ce focus-la ne doit pas rouvrir la carte.
  const refocusing = useRef(false);
  const close = () => {
    const id = selectedId;
    setSelectedId(null);
    if (id !== null) {
      refocusing.current = true;
      buttons.current.get(id)?.focus();
      refocusing.current = false;
    }
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && selectedId !== null) {
      event.stopPropagation();
      close();
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="frise-horizontale">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Echap ferme la carte ouverte, ou que soit le focus dans la frise. */}
      <div className="-mx-4 overflow-x-auto px-4" onKeyDown={onKeyDown}>
        <div className="relative" style={{ width: total, height }}>
          <svg
            aria-hidden="true"
            className="absolute inset-0"
            height={height}
            viewBox={`0 0 ${String(total)} ${String(height)}`}
            width={total}
          >
            <defs>
              <linearGradient id="tl-thread" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#F3EEE3" stopOpacity="0" />
                <stop offset="0.04" stopColor="#F3EEE3" stopOpacity="0.85" />
                <stop offset="0.96" stopColor="#F3EEE3" stopOpacity="0.85" />
                <stop offset="1" stopColor="#F3EEE3" stopOpacity="0" />
              </linearGradient>
              <filter height="300%" id="tl-glow" width="120%" x="-10%" y="-100%">
                <feGaussianBlur stdDeviation="3" />
              </filter>
            </defs>
            {stars(46, total, height, threadY).map((star) => (
              <circle
                cx={star.x}
                cy={star.y}
                fill="#FFFFFF"
                key={star.key}
                opacity={star.opacity}
                r={star.r}
              />
            ))}
            {selected ? (
              <rect
                data-testid="zone-eclairee"
                fill={colorOf(tracks, selected.entry)}
                fillOpacity="0.25"
                height="22"
                rx="11"
                width={Math.max(selected.highlight.to - selected.highlight.from, 10)}
                x={
                  SIDE +
                  selected.highlight.from -
                  (selected.highlight.to === selected.highlight.from ? 5 : 0)
                }
                y={threadY - 11}
              />
            ) : null}
            <rect fill="url(#tl-thread)" height="2" width={total} x="0" y={threadY - 1} />
            <rect
              fill="url(#tl-thread)"
              filter="url(#tl-glow)"
              height="2"
              opacity="0.5"
              width={total}
              x="0"
              y={threadY - 1}
            />
            {placed
              .filter((item) => item.band !== null)
              .map((item, index) => {
                const band = item.band as { from: number; to: number };
                const color = colorOf(tracks, item.entry);
                const gradientId = `tl-band-${String(item.entry.id)}`;
                return (
                  <g key={`band-${String(item.entry.id)}`}>
                    {item.entry.ongoing ? (
                      <defs>
                        <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
                          <stop offset="0.6" stopColor={color} stopOpacity="0.75" />
                          <stop offset="1" stopColor={color} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    ) : null}
                    <rect
                      fill={item.entry.ongoing ? `url(#${gradientId})` : color}
                      height="3"
                      opacity={item.entry.ongoing ? 1 : 0.75}
                      rx="1.5"
                      width={Math.max(band.to - band.from, 3)}
                      x={SIDE + band.from}
                      y={threadY + 6 + (index % 2) * 6}
                    />
                  </g>
                );
              })}
            {ticks.map((tick) => (
              <text
                fill="#8C96AA"
                fontFamily="Fraunces, Georgia, serif"
                fontSize="13"
                key={tick.year}
                textAnchor="middle"
                x={SIDE + tick.x}
                y={threadY + 34}
              >
                {tick.year}
              </text>
            ))}
            {placed.map((item) => {
              const color = colorOf(tracks, item.entry);
              const y = ballY(item);
              return (
                <g key={`stem-${String(item.entry.id)}`}>
                  <line
                    stroke={color}
                    strokeOpacity="0.7"
                    x1={SIDE + item.x}
                    x2={SIDE + item.x}
                    y1={y}
                    y2={threadY}
                  />
                  <circle cx={SIDE + item.x} cy={threadY} fill="#F3EEE3" r="3" />
                </g>
              );
            })}
          </svg>

          {placed.map((item) => {
            const color = colorOf(tracks, item.entry);
            const isSelected = item.entry.id === selectedId;
            const right = item.labelSide === "right";
            return (
              <button
                aria-controls={isSelected ? "tl-carte" : undefined}
                aria-expanded={isSelected}
                aria-label={accessibleName(item.entry)}
                className={`absolute flex min-h-11 items-center gap-0 rounded-full p-0 text-left ${right ? "" : "flex-row-reverse text-right"}`}
                key={item.entry.id}
                onClick={() => setSelectedId(item.entry.id)}
                onFocus={() => {
                  if (!refocusing.current) setSelectedId(item.entry.id);
                }}
                onMouseEnter={() => setSelectedId(item.entry.id)}
                ref={(node) => {
                  if (node) buttons.current.set(item.entry.id, node);
                  else buttons.current.delete(item.entry.id);
                }}
                style={{
                  left: SIDE + item.x + (right ? -22 : 22),
                  top: ballY(item) - 22,
                  transform: right ? undefined : "translateX(-100%)",
                }}
                type="button"
              >
                <span className="flex size-11 shrink-0 items-center justify-center">
                  <span
                    className="box-border size-5 rounded-full"
                    style={ballStyle(color, isFuzzy(item.entry), isSelected)}
                  />
                </span>
                <span
                  aria-hidden="true"
                  className="flex flex-col whitespace-nowrap rounded-md bg-[rgba(13,18,34,0.72)] px-1.5 py-0.5"
                  style={{ maxWidth: LABEL_MAX + 12 }}
                >
                  <span className="truncate text-[13px] font-medium text-foreground">
                    {item.entry.title}
                  </span>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {whenLabel(item.entry)}
                  </span>
                </span>
              </button>
            );
          })}

          {selected ? (
            <section
              aria-label={selected.entry.title}
              className="absolute z-10 box-border flex flex-col gap-1.5 rounded-[14px] border border-[rgba(243,238,227,0.16)] bg-card px-5 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.45)]"
              id="tl-carte"
              style={{
                width: CARD_WIDTH,
                left: cardLeft(selected, total),
                top: Math.max(
                  0,
                  Math.min(selected.level > 0 ? ballY(selected) - 40 : threadY + 24, height - 230),
                ),
              }}
            >
              <EntryCardBody
                entry={selected.entry}
                layout="bubble"
                onClose={close}
                onEdit={onEdit === undefined ? undefined : () => onEdit(selected.entry)}
                track={trackOf(tracks, selected.entry)}
              />
            </section>
          ) : null}
        </div>
      </div>
      <Legend tracks={tracks} />
    </div>
  );
}

/** La legende : chaque piste par son nom, et la boule cerclee de la date approximative. */
export function Legend({ tracks }: { tracks: readonly Track[] }) {
  return (
    <ul
      aria-label="Légende des pistes"
      className="m-0 flex list-none flex-wrap gap-4 p-0 text-[13px] text-muted-foreground"
    >
      {tracks.map((track) => (
        <li className="inline-flex items-center gap-1.5" key={track.id}>
          <span
            aria-hidden="true"
            className="size-2.5 rounded-full"
            style={{ background: track.color, boxShadow: `0 0 6px ${track.color}` }}
          />
          {track.name}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="box-border size-2.5 rounded-full border-2 border-dashed border-muted-foreground"
        />
        date approximative
      </li>
    </ul>
  );
}

function FriseVertical({ entries, tracks, onEdit }: FriseProps) {
  const dated = datedInOrder(entries);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const buttons = useRef(new Map<number, HTMLButtonElement>());
  const selected = dated.find((entry) => entry.id === selectedId) ?? null;

  const close = () => {
    const id = selectedId;
    setSelectedId(null);
    if (id !== null) buttons.current.get(id)?.focus();
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Echap ferme la feuille ouverte, ou que soit le focus dans la frise.
    <div
      className="flex flex-col gap-3"
      data-testid="frise-verticale"
      onKeyDown={(event) => {
        if (event.key === "Escape" && selectedId !== null) close();
      }}
    >
      <div className="relative py-3">
        <div
          aria-hidden="true"
          className="absolute top-0 bottom-0 left-1/2 w-0.5 -translate-x-1/2"
          style={{
            background:
              "linear-gradient(to bottom, rgba(243,238,227,0.85), rgba(243,238,227,0.85) 85%, rgba(243,238,227,0))",
            boxShadow: "0 0 10px rgba(243,238,227,0.35)",
          }}
        />
        <ol className="relative m-0 flex list-none flex-col p-0">
          {dated.map((entry, index) => {
            const color = colorOf(tracks, entry);
            const isSelected = entry.id === selectedId;
            const side = verticalSide(index);
            const label = (
              <div
                aria-hidden="true"
                className={`flex min-w-0 flex-col gap-0.5 ${side === "left" ? "text-right" : ""}`}
              >
                <span className="text-[15px] font-bold" style={{ color }}>
                  {whenLabel(entry)}
                </span>
                <span className="text-[13px] leading-snug text-foreground">{entry.title}</span>
              </div>
            );
            return (
              <li className="grid min-h-[66px] grid-cols-[1fr_44px_1fr] gap-x-1.5" key={entry.id}>
                <div>{side === "left" ? label : null}</div>
                <button
                  aria-controls={isSelected ? "tl-feuille" : undefined}
                  aria-expanded={isSelected}
                  aria-label={accessibleName(entry)}
                  className="flex w-11 items-start justify-center self-start rounded-full p-0"
                  onClick={() => setSelectedId(entry.id)}
                  ref={(node) => {
                    if (node) buttons.current.set(entry.id, node);
                    else buttons.current.delete(entry.id);
                  }}
                  style={{ minHeight: entry.kind === "period" ? 52 : 44 }}
                  type="button"
                >
                  <span
                    className="mt-1.5 box-border w-4 rounded-full"
                    style={{
                      height: entry.kind === "period" ? 34 : 16,
                      ...ballStyle(color, isFuzzy(entry), isSelected),
                    }}
                  />
                </button>
                <div>{side === "right" ? label : null}</div>
              </li>
            );
          })}
        </ol>
      </div>
      <Legend tracks={tracks} />
      {selected ? (
        <section
          aria-label={selected.title}
          className="fixed inset-x-3 bottom-[72px] z-30 flex flex-col gap-1.5 rounded-[18px] border border-[rgba(243,238,227,0.16)] bg-card px-[18px] py-4 shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
          id="tl-feuille"
        >
          <EntryCardBody
            entry={selected}
            layout="sheet"
            onClose={close}
            onEdit={onEdit === undefined ? undefined : () => onEdit(selected)}
            track={trackOf(tracks, selected)}
          />
        </section>
      ) : null}
    </div>
  );
}
